import React, { useEffect } from 'react';
import { useFormField } from '../../core';
import clsx from 'clsx';
import { RadioOption } from '../../typings';
import { Icon, RadioGroup as UIRadioGroup } from '@/ui-kit';
import './RadioGroup.scss';
import { useAppDispatch, useAppSelector } from '@/hooks';
import { selectForm, setFormFieldError } from '@/forms/store';

type RadioGroupProps = {
  label: string;
  name: string;
  options?: RadioOption[];
  stateDefaultIcon?: React.ReactNode;
  orientation?: React.ComponentProps<typeof UIRadioGroup>['orientation'];
  boxedLayout?: React.ComponentProps<typeof UIRadioGroup>['boxedLayout'];
  scale?: React.ComponentProps<typeof UIRadioGroup>['size'];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [x: string]: any;
};

type StateType = 'default' | 'error' | 'warning' | 'disabled';

export const RadioGroup: React.FC<RadioGroupProps> = ({
  label,
  options,
  stateDefaultIcon,
  orientation,
  boxedLayout = true,
  scale = 'medium',
  ...props
}) => {
  const [field, meta] = useFormField(props);
  const { errorRegistry } = useAppSelector(selectForm);

  const manualError = errorRegistry?.[field.name];

  const dispatch = useAppDispatch();

  const error = manualError ?? (meta.touched ? meta.error : undefined);

  const state: StateType = error ? 'error' : 'default';
  const stateText = error;

  const iconColorRegistry = {
    default: 'var(--color_primary-dark)',
    error: 'var(--color_error-dark)',
    warning: 'var(--color_warning)',
    disabled: 'var(--color_disabled)',
  };

  const stateTextClasses = clsx('state-text', {
    'state-text--error': state === 'error',
    'state-text--disabled': props.disabled,
  });

  useEffect(() => {
    const trackableOptions = options?.filter(o => o.trackValue);

    if (trackableOptions?.some(o => o.value === field.value)) {
      const selectedOption = trackableOptions.find(
        o => o.value === field.value,
      );
      dispatch(
        setFormFieldError({
          [field.name]: selectedOption?.errorMessage ?? 'Error',
        }),
      );
    } else {
      dispatch(setFormFieldError({ [field.name]: null }));
    }
  }, [options, field.value, field.name, dispatch]);

  return (
    <div className="rg__container">
      <UIRadioGroup
        title={label}
        id={field.name}
        size={scale}
        choices={
          options?.map(opt => ({
            key: opt.label,
            value: opt.value.toString(),
            disabled: props.disabled ?? false,
            kind: opt.kind,
          })) ?? [
            {
              key: 'Placeholder option',
              value: '',
              disabled: false,
            },
          ]
        }
        {...props}
        group={props.name}
        {...field}
        value={field.value as string | undefined}
        orientation={
          orientation ?? (options && options.length > 2 ? 'column' : 'row')
        }
        boxedLayout={boxedLayout}
        error={state === 'error'}
      />
      {stateText && (
        <div className="rg__bottom-wrapper">
          <div className="left">
            {state === 'default' && stateDefaultIcon ? (
              stateDefaultIcon
            ) : (
              <Icon
                type={state === 'error' ? 'x' : null}
                color={
                  props.disabled === true
                    ? iconColorRegistry.disabled
                    : iconColorRegistry[state]
                }
              />
            )}

            <div className={stateTextClasses}>{stateText}</div>
          </div>
        </div>
      )}
    </div>
  );
};
